import { useStorage, useSessionStorage } from '@vueuse/core';

/**
 * All persisted state for the depot tours: what has been seen, whether tours are muted,
 * and how many have auto-started this session.
 *
 * Deliberately NOT a pinia store, even though fourteen stores in this app are. Two
 * separate mechanisms in `user.store.ts` would destroy this on sign-out:
 *
 *   - `PERSISTED_STORE_IDS` lists the store ids whose localStorage and sessionStorage
 *     entries are removed on logout. Staying off that list is not enough, because:
 *   - `resetActiveStores()` calls `reset()` on every active store except `app`, and for
 *     a `persist: true` store the persistence plugin then writes the emptied state
 *     straight back to storage.
 *
 * Getting it right as a store would mean remembering to touch both that list and
 * `STORE_IDS_TO_SKIP_RESET`, and a later edit to either silently re-fires every tour for
 * everyone. A plain storage key is invisible to both.
 *
 * Everything goes through this module, so moving to per-user server-side state later is a
 * change of adapter rather than a change of design.
 */

const SEEN_KEY = 'depot.tours.seen';
const MUTED_KEY = 'depot.tours.muted';
const AUTOSTART_KEY = 'depot.tours.autoStarts';

/**
 * At most this many tours start on their own per browser session. A new operator opening
 * a dozen pages in one sitting should be interrupted a few times, not a dozen. A tour the
 * cap suppresses is NOT marked seen, so it offers itself again in a later session.
 */
export const MAX_AUTO_STARTS_PER_SESSION = 3;

/** Tour key to the version of that tour the operator last saw. */
const seen = useStorage<Record<string, number>>(SEEN_KEY, {});
const muted = useStorage<boolean>(MUTED_KEY, false);
const autoStarts = useSessionStorage<number>(AUTOSTART_KEY, 0);

/**
 * A version rather than a boolean, so raising a tour's version in the registry shows it
 * again to people who saw the older one. A boolean would make every tour a one-way door:
 * rewrite the copy and nobody who already dismissed it ever sees the correction.
 */
export const hasSeen = (key: string, version: number): boolean => {
	return (seen.value[key] ?? 0) >= version;
};

/**
 * Called only when a tour actually ran, was dismissed, or had nothing left to show. Never
 * called when a tour was skipped for a reason that might not hold next time — a timeout
 * waiting for anchors, or the session cap — because that would cost the page its tour
 * permanently on the strength of one slow response.
 */
export const markSeen = (key: string, version: number): void => {
	seen.value = { ...seen.value, [key]: version };
};

export const isMuted = (): boolean => {
	return muted.value;
};

export const muteAll = (): void => {
	muted.value = true;
};

export const unmuteAll = (): void => {
	muted.value = false;
};

export const autoStartsRemaining = (): number => {
	return Math.max(0, MAX_AUTO_STARTS_PER_SESSION - autoStarts.value);
};

export const recordAutoStart = (): void => {
	autoStarts.value += 1;
};

/** Test and development helper: forget everything so the tours can be seen again. */
export const resetAllTourState = (): void => {
	seen.value = {};
	muted.value = false;
	autoStarts.value = 0;
};
